import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** Índice = dia da semana em `Date.getDay()`: 0=domingo … 6=sábado. */
export const WEEKDAY_KEYS = [
  "fixedDays.weekday.sunday",
  "fixedDays.weekday.monday",
  "fixedDays.weekday.tuesday",
  "fixedDays.weekday.wednesday",
  "fixedDays.weekday.thursday",
  "fixedDays.weekday.friday",
  "fixedDays.weekday.saturday",
] as const;

export interface FixedWeekdaysFieldProps {
  value: number[];
  /** Quantidade exigida pelo plano (`usage_limit`). */
  required: number;
  onChange: (weekdays: number[]) => void;
  disabled?: boolean;
  /** Esconde o texto de contagem ("N dias selecionados"/"Selecione mais N dias"). */
  hideStatus?: boolean;
}

export function FixedWeekdaysField({
  value,
  required,
  onChange,
  disabled = false,
  hideStatus = false,
}: FixedWeekdaysFieldProps) {
  const { t } = useTranslation("daycareHotel");
  const missing = Math.max(0, required - value.length);
  const atLimit = value.length >= required;

  const toggle = (weekday: number) => {
    if (disabled) return;
    const selected = value.includes(weekday);
    if (!selected && atLimit) return;
    const next = selected
      ? value.filter((day) => day !== weekday)
      : [...value, weekday].sort((a, b) => a - b);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label={t("fixedDays.groupLabel")} className="flex flex-wrap gap-1.5">
        {WEEKDAY_KEYS.map((key, weekday) => {
          const selected = value.includes(weekday);
          return (
            <button
              key={key}
              type="button"
              aria-pressed={selected}
              disabled={disabled || (!selected && atLimit)}
              onClick={() => toggle(weekday)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50",
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-foreground hover:bg-accent",
              )}
            >
              {t(key)}
            </button>
          );
        })}
      </div>
      {hideStatus ? null : (
        <p role="status" className={cn("text-xs", missing > 0 ? "text-muted-foreground" : "text-primary")}>
          {missing > 0 ? t("fixedDays.remaining", { count: missing }) : t("fixedDays.complete", { count: required })}
        </p>
      )}
    </div>
  );
}
